<template>
  <div>

    <p class="tab-hint">
      Produtos disponíveis na plataforma. Ajuste o status e as datas direto no card:
      as alterações são gravadas todas juntas no botão Salvar, no fim da página.
    </p>

    <p v-if="!catalog.length" class="empty">Carregando produtos…</p>

    <div v-for="entry in catalog" :key="entry.app.id"
         class="product"
         :class="{ 'marker-yellow': entry.changed, inactive: !entry.accountApp }">

      <div class="product-head">
        <span class="product-dot" :class="dotClass(entry)"/>
        <span class="product-name">{{ displayName(entry.app) }}</span>

        <select :value="entry.accountApp ? entry.accountApp.status : ''"
                @change="changeStatus(entry, $event)"
                class="form-control status-select">
          <option v-if="!entry.accountApp" value="" disabled>não contratado</option>
          <option v-for="option in optionsFor(entry)" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-if="entry.accountApp" class="product-dates">
        <div class="product-field">
          <label>Status</label>
          <span class="product-static">{{ savedStatusLabel(entry) }}</span>
        </div>

        <div class="product-field">
          <label>Solicitado em</label>
          <span class="product-static">{{ formatDate(entry.accountApp.created_at) }}</span>
        </div>

        <div class="product-field">
          <label>Ativado em</label>
          <span class="product-static">{{ formatDate(entry.accountApp.subscription_at) }}</span>
        </div>

        <div class="product-field">
          <label :for="`expiration-${entry.app.id}`">Expira em</label>
          <input :id="`expiration-${entry.app.id}`"
                 :value="dateValue(entry.accountApp.expiration_at)"
                 :disabled="!acceptsExpiration(entry.accountApp.status)"
                 @input="updateExpiration(entry.accountApp, $event.target.value)"
                 type="date" class="form-control">
        </div>

        <div class="product-field">
          <label>Cancelado em</label>
          <span class="product-static">{{ formatDate(entry.accountApp.canceled_at) }}</span>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
  import { getAppDisplayName } from '@/appDisplayNames'
  import {
    ACTIVE_STATUSES,
    PRODUCT_STATUS_OPTIONS,
    dateOnly,
    dependencyError,
    productChanged,
    restoreProductFields,
    statusLabel,
    trialExpiration
  } from '@/models/accountProducts'

  export default {
    computed: {
      accountApps() {
        return this.account.account_apps || []
      },
      activeNames() {
        return this.apps
          .filter(app => {
            const accountApp = this.findAccountApp(app.id)
            return !!accountApp && ACTIVE_STATUSES.includes(accountApp.status)
          })
          .map(app => app.name)
      },
      catalog() {
        return this.apps.map(app => {
          const accountApp = this.findAccountApp(app.id)

          return {
            app: app,
            accountApp: accountApp,
            changed: this.isChanged(app.id, accountApp)
          }
        })
      },
      statusOptions() {
        return PRODUCT_STATUS_OPTIONS
      }
    },
    methods: {
      statusLabel,
      acceptsExpiration(status) {
        return status === 'trial'
      },
      allowedFor(entry, status) {
        const current = entry.accountApp ? entry.accountApp.status : ''
        return (this.transitions[current] || []).includes(status)
      },
      optionsFor(entry) {
        const current = entry.accountApp ? entry.accountApp.status : ''

        return PRODUCT_STATUS_OPTIONS.filter(option =>
          option.value === current || this.allowedFor(entry, option.value))
      },
      savedStatusLabel(entry) {
        const original = this.originalOf(entry.app.id)

        return original ? statusLabel(original.status) : 'não contratado'
      },
      changeStatus(entry, event) {
        const status = event.target.value
        const current = entry.accountApp ? entry.accountApp.status : ''

        if (!status || !this.allowedFor(entry, status))
          return

        const blocked = dependencyError(entry.app.name, status, this.activeNames)
        if (blocked) {
          event.target.value = current
          return this.$notify.addError(blocked)
        }

        const accountApp = entry.accountApp || this.createAccountApp(entry.app)
        const original = this.originalOf(entry.app.id)

        accountApp.status = status

        if (original && original.status === status)
          return restoreProductFields(accountApp, original)

        this.applyStatusEffects(accountApp, status)
      },
      applyStatusEffects(accountApp, status) {
        const now = new Date().toISOString()

        if (status !== 'canceled')
          accountApp.canceled_at = null

        switch (status) {
          case 'trial':
            accountApp.active = true
            accountApp.trial_at = accountApp.trial_at || now
            accountApp.expiration_at = accountApp.expiration_at || trialExpiration()
            break
          case 'installed':
            accountApp.active = true
            accountApp.subscription_at = now
            accountApp.expiration_at = null
            break
          case 'canceled':
            accountApp.active = false
            accountApp.canceled_at = now
            break
          default:
            accountApp.active = false
        }
      },
      createAccountApp(app) {
        const accountApp = {
          id: null,
          app_id: app.id,
          nickname: this.displayName(app),
          status: null,
          active: false,
          trial_at: null,
          expiration_at: null,
          subscription_at: null,
          canceled_at: null,
          created_at: new Date().toISOString()
        }

        this.accountApps.push(accountApp)
        return accountApp
      },
      dateValue(value) {
        return dateOnly(value) || ''
      },
      displayName(app) {
        return getAppDisplayName(app.name) || app.nickname || app.name
      },
      dotClass(entry) {
        return entry.accountApp ? `dot-${entry.accountApp.status}` : 'dot-none'
      },
      findAccountApp(appId) {
        return this.accountApps.find(accountApp => accountApp.app_id === appId)
      },
      formatDate(value) {
        return value ? this.$options.filters.date(value) : '-'
      },
      isChanged(appId, accountApp) {
        return productChanged(accountApp, this.originalOf(appId))
      },
      originalOf(appId) {
        return this.pristineApps.find(item => item.app_id === appId)
      },
      updateExpiration(accountApp, value) {
        accountApp.expiration_at = value || null
      }
    },
    name: 'TabProducts',
    props: {
      account: { required: true, type: Object },
      apps: { default: () => [], type: Array },
      pristineApps: { default: () => [], type: Array },
      transitions: { default: () => ({}), type: Object }
    }
  }
</script>

<style scoped>
  .tab-hint {
    margin-bottom: 20px;
    font-size: 13px;
    color: var(--secondary-text-color);
  }

  .empty {
    color: var(--secondary-text-color);
  }

  .product {
    padding: 16px 20px;
    margin-bottom: 12px;
    border: 1px solid var(--border-color);
    border-radius: 5px;
    background: var(--card-bg);
  }

  .product.inactive {
    background: var(--light-gray);
  }

  .product.marker-yellow {
    border-left: solid 5px #FFC866;
    border-radius: 5px 0 0 5px;
  }

  .product-head {
    display: flex;
    align-items: center;
  }

  .product-dot {
    width: 10px;
    height: 10px;
    margin-right: 10px;
    border-radius: 50%;
    background: var(--border-color);
  }

  .dot-none      { background: var(--border-color); }
  .dot-requested { background: #FFC866; }
  .dot-expired   { background: #F0803C; }
  .dot-trial     { background: #2D98DA; }
  .dot-installed { background: #28A745; }
  .dot-canceled  { background: #D9534F; }

  .product-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--primary-text-color);
  }

  .status-select {
    width: 170px;
    height: 34px;
    margin-left: auto;
    padding: 2px 8px;
    font-size: 13px;
  }

  .product-dates {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    margin-top: 12px;
  }

  .product-field {
    display: flex;
    flex-direction: column;
  }

  .product-field label {
    margin-bottom: 2px;
    font-size: 12px;
    color: var(--secondary-text-color);
  }

  .product-field .form-control {
    width: 170px;
    height: 34px;
    font-size: 13px;
  }

  .product-static {
    height: 34px;
    display: flex;
    align-items: center;
    font-size: 13px;
    color: var(--primary-text-color);
  }
</style>
